"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLatestChanges = exports.useSharedValue = exports.useWaitForSharedValue = exports.useModifySharedValue = exports.useReadSharedValue = exports.useSharedValueInitialization = void 0;
const lodash_isequal_1 = __importDefault(require("lodash.isequal"));
const react_1 = require("react");
const use_shared_collection_1 = require("./use-shared-collection");
const use_singleton_initialization_1 = require("./use-singleton-initialization");
const shared_value_resolving_1 = require("./shared-value-resolving");
const shared_values_context_1 = require("./context/shared-values-context");
/**
 * This hook defines an async initialization that will only be called once globally.
 * This allows a developer to define the init logic of a shared value inside it's hook
 * without initialization being triggered multiple times
 * @deprecated Use initializers instead
 * @param key The shared value key
 * @param init An async initialization function that returns the inital value
 */
const useSharedValueInitialization = (key, init) => {
    const setValue = useModifySharedValue(key);
    const apply = (value) => setValue(value);
    return use_singleton_initialization_1.useSingletonInitialization(key, init, apply);
};
exports.useSharedValueInitialization = useSharedValueInitialization;
function useReadSharedValue(key) {
    if (isValueFunction(key)) {
        return useReadFunction(key);
    }
    else if (isCollectionKey(key)) {
        return use_shared_collection_1.useReadCollectionValue(key);
    }
    else {
        return useReadSingleSharedValue(key);
    }
}
exports.useReadSharedValue = useReadSharedValue;
function useModifySharedValue(key, collectionValueKey) {
    if (!isCollectionKey(key)) {
        return useModifySingleSharedValue(key);
    }
    else {
        const collection = use_shared_collection_1.useModifySharedCollection(key);
        return react_1.useCallback((value) => collection.setValue(collectionValueKey, value), [
            collection,
            collectionValueKey
        ]);
    }
}
exports.useModifySharedValue = useModifySharedValue;
function useWaitForSharedValue(key, criteria) {
    if (isValueFunction(key)) {
        return useWaitForFunction(key);
    }
    else if (!isCollectionKey(key)) {
        return useWaitForSingleSharedValue(key, criteria);
    }
    else {
        return use_shared_collection_1.useWaitForCollectionValue(key, criteria);
    }
}
exports.useWaitForSharedValue = useWaitForSharedValue;
function useWaitForFunction(func, criteria) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    return function waitForFunction() {
        return context.waitFor(func, criteria);
    };
}
function useWaitForSingleSharedValue(key, criteria) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    return function waitForValue() {
        return context.waitFor(key, criteria);
    };
}
function useSharedValue(key, collectionValueKeyOrArgs) {
    if (isValueFunction(key)) {
        return useValueFunction(key, collectionValueKeyOrArgs);
    }
    else if (!isCollectionKey(key)) {
        return useSingleSharedValue(key);
    }
    else if (isResolvedCollectionKey(key)) {
        return shared_value_resolving_1.useResolvedCollectionValue(key, collectionValueKeyOrArgs);
    }
    else {
        return use_shared_collection_1.useSingleCollectionValue(key, collectionValueKeyOrArgs);
    }
}
exports.useSharedValue = useSharedValue;
function useValueFunction(func, volatileArgs = []) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    const memoFunc = react_1.useCallback(func, volatileArgs);
    const [value, setValue] = react_1.useState(() => context.get(memoFunc));
    react_1.useEffect(() => {
        const newValue = context.get(memoFunc);
        if (!lodash_isequal_1.default(value, newValue))
            setValue(newValue);
    }, [context, memoFunc]);
    useLatestChanges(() => context.changes(memoFunc), newValue => {
        setValue(newValue);
    }, [context, memoFunc]);
    return value;
}
function useSingleSharedValue(key) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    const [value, setValue] = react_1.useState(() => context.get(key));
    react_1.useEffect(updateWithStoreValue, [context]);
    useLatestChanges(() => context.changes(key), newValue => {
        setValue(newValue);
    }, [context]);
    const setStoreValue = react_1.useCallback((value) => context.set(key, value), [context]);
    function updateWithStoreValue() {
        const storeValue = context.get(key);
        if (storeValue !== value) {
            setValue(storeValue);
        }
    }
    return [value, setStoreValue];
}
function isValueFunction(key) {
    return typeof key === 'function';
}
function isCollectionKey(key) {
    return key['collection'] === true;
}
function isResolvedCollectionKey(key) {
    return key.resolver !== undefined;
}
function useModifySingleSharedValue(key) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    return react_1.useCallback((value) => context.set(key, value), [context]);
}
function useReadFunction(func) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    return react_1.useCallback(() => context.get(func), [context, func]);
}
function useReadSingleSharedValue(key) {
    const context = shared_values_context_1.useSharedValueStoreContext();
    return react_1.useCallback(() => context.get(key), [context, key]);
}
function useLatestChanges(changes, onChanges, volatileArgs = []) {
    react_1.useEffect(() => {
        const sub = changes().subscribe(onChanges);
        return () => sub.unsubscribe();
    }, volatileArgs);
}
exports.useLatestChanges = useLatestChanges;
//# sourceMappingURL=use-shared-value.js.map